import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';

const ROWS_PER_PAGE = 25;

const DomesticBuyerList = () => {
  const [buyers, setBuyers] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchBuyers();
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const fetchBuyers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: debouncedSearch.trim()
      });
      const res = await fetch(`${API_BASE_URL}/api/masters/domestic-buyer-get?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setBuyers(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      setError('Failed to load data');
      console.error('Fetch domestic buyer error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">Domestic Buyer Records</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="border border-gray-200 rounded px-2 py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            onClick={() => navigate('/domestic-buyer/new')}
            className="flex items-center gap-1 bg-[#10a5e9] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : buyers.length === 0 ? (
        <NoRecordFound message="No domestic buyer records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Name</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Contact Person</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Phone</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Email</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Address</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">GST No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">PAN No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">State Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Account No</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">IFSC Code</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Bank Name</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Bank Address</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {buyers.map((b, i) => (
                <tr key={b.domestic_buyer_id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  
                  <td className="px-3 py-1 font-semibold whitespace-nowrap">{b.name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.contact_person}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.phone}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.email}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.address}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.gst_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.pan_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.state}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.state_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.account_no}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.ifsc_code}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.bank_name}</td>
                  <td className="px-3 py-1 whitespace-nowrap">{b.bank_address}</td>
                  <td className="px-3 py-1">
                    <button
                      onClick={() => navigate(`/domestic-buyer/edit/${b.domestic_buyer_id}`)}
                      className="text-xs px-2 py-1 bg-[#10a5e9] text-white rounded hover:bg-[#0e8dc6] border border-[#10a5e9] flex items-center gap-1"
                      title="Edit Domestic Buyer"
                    >
                      <FaEdit />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNext}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default DomesticBuyerList;
